import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export interface QuoteBasketItem {
  id: string;
  name: string;
  image?: string | null;
  location?: string | null;
  /** supplier_type of the listing, e.g. "venue", "photographer" */
  type?: string | null;
}

const STORAGE_KEY = "quote-basket-v1";
const MAX_ITEMS = 10;

interface QuoteBasketValue {
  items: QuoteBasketItem[];
  has: (id: string) => boolean;
  add: (item: QuoteBasketItem) => boolean;
  remove: (id: string) => void;
  toggle: (item: QuoteBasketItem) => void;
  clear: () => void;
  max: number;
}

const QuoteBasketContext = createContext<QuoteBasketValue | null>(null);

const read = (): QuoteBasketItem[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((i) => i && typeof i.id === "string") : [];
  } catch {
    return [];
  }
};

export const QuoteBasketProvider = ({ children }: { children: React.ReactNode }) => {
  const [items, setItems] = useState<QuoteBasketItem[]>(() =>
    typeof window === "undefined" ? [] : read(),
  );

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* storage can be unavailable in private mode */
    }
  }, [items]);

  const has = useCallback((id: string) => items.some((i) => i.id === id), [items]);

  const add = useCallback((item: QuoteBasketItem) => {
    let added = false;
    setItems((prev) => {
      if (prev.some((i) => i.id === item.id)) return prev;
      if (prev.length >= MAX_ITEMS) return prev;
      added = true;
      return [...prev, item];
    });
    return added;
  }, []);

  const remove = useCallback((id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const toggle = useCallback((item: QuoteBasketItem) => {
    setItems((prev) =>
      prev.some((i) => i.id === item.id)
        ? prev.filter((i) => i.id !== item.id)
        : prev.length >= MAX_ITEMS
          ? prev
          : [...prev, item],
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const value = useMemo(
    () => ({ items, has, add, remove, toggle, clear, max: MAX_ITEMS }),
    [items, has, add, remove, toggle, clear],
  );

  return <QuoteBasketContext.Provider value={value}>{children}</QuoteBasketContext.Provider>;
};

export const useQuoteBasket = () => {
  const ctx = useContext(QuoteBasketContext);
  if (!ctx) throw new Error("useQuoteBasket must be used inside QuoteBasketProvider");
  return ctx;
};
